import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import React from 'react';

export interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    adjacentPages?: number;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    className,
    adjacentPages = 2
}) => {
    if (totalPages <= 1) {
        return null;
    }

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages - 1) {
            onPageChange(currentPage + 1);
        }
    };

    const handlePageClick = (pageIndex: number) => {
        onPageChange(pageIndex);
    };

    return (
        <div className={clsx('flex items-center justify-center gap-3', className)}>
            <button
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
                className={clsx(
                    'min-w-[32px] h-8 px-2 rounded-md text-sm font-medium transition-colors',
                    currentPage === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
            >
                <ChevronLeftIcon className='w-4 h-4' />
            </button>
            <div className='flex items-center gap-1.5'>
                {Array.from({ length: totalPages }, (_, i) => i).map((pageIndex) => {
                    // Show first page, last page, current page and adjacent pages
                    const isFirst = pageIndex === 0;
                    const isLast = pageIndex === totalPages - 1;
                    const isCurrent = pageIndex === currentPage;
                    const isAdjacent = Math.abs(pageIndex - currentPage) <= adjacentPages;
                    
                    if (isFirst || isLast || isCurrent || isAdjacent) {
                        return (
                            <button
                                key={pageIndex}
                                onClick={() => handlePageClick(pageIndex)}
                                className={clsx(
                                    'min-w-[32px] h-8 px-2 rounded-md text-sm font-medium transition-colors',
                                    isCurrent 
                                        ? 'bg-primary-500 text-white' 
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                )}
                            >
                                {pageIndex + 1}
                            </button>
                        );
                    } else if (
                        (pageIndex === 1 && currentPage - adjacentPages > 1) ||
                        (pageIndex === totalPages - 2 && currentPage + adjacentPages < totalPages - 2)
                    ) {
                        return <span key={pageIndex} className='text-gray-400 px-1'>...</span>;
                    }
                    return null;
                })}
            </div>
            <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages - 1}
                className={clsx(
                    'min-w-[32px] h-8 px-2 rounded-md text-sm font-medium transition-colors',
                    currentPage === totalPages - 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
            >
                <ChevronRightIcon className='w-4 h-4' />
            </button>
        </div>
    );
};
